/**
 * @fileoverview PDF Content Structure Contracts
 * @module contracts/pdf-content
 *
 * Defines the structured format for PDF content rendering.
 * This is the source of truth for all PDF content, independent of rendering format.
 *
 * Design Pattern: Strategy Pattern + Adapter Pattern
 * - Content is always stored/transmitted as StructuredPdfContent
 * - Parsers convert different formats (JSON, Markdown) to StructuredPdfContent
 * - Renderers convert StructuredPdfContent to different outputs (PDF, HTML, etc.)
 */

import { z } from 'zod';

/**
 * Content block types for structured PDF content
 */
export const ContentBlockTypeSchema = z.enum([
	'paragraph',
	'heading',
	'ordered_list',
	'unordered_list',
	'emphasis',
]);
export type ContentBlockType = z.infer<typeof ContentBlockTypeSchema>;

/**
 * Text content with optional formatting
 */
export const TextContentSchema = z
	.object({
		text: z.string().min(1),
		bold: z.boolean().nullable(),
		italic: z.boolean().nullable(),
	})
	.strict();
export type TextContent = z.infer<typeof TextContentSchema>;

/**
 * List item with title and description
 */
export const ListItemSchema = z
	.object({
		text: z.string().nullable(),
		title: TextContentSchema.nullable(),
		description: TextContentSchema.nullable(),
	})
	.strict();
export type ListItem = z.infer<typeof ListItemSchema>;

/**
 * Content block - represents a single piece of content
 * Using a single object schema with nullable fields for OpenAI compatibility
 * (OpenAI's structured output requires all fields to be required or nullable, not optional)
 */
export const ContentBlockSchema = z
	.object({
		type: ContentBlockTypeSchema,
		text: z.string().nullable(),
		level: z.number().int().min(1).max(6).nullable(),
		content: TextContentSchema.nullable(),
		items: z.array(ListItemSchema).nullable(),
	})
	// Ensure type is always present
	.refine((data) => data.type !== undefined, {
		message: 'type is required',
	})
	// Validate specific fields based on type
	.superRefine((data, ctx) => {
		if (data.type === 'paragraph' || data.type === 'emphasis') {
			if (data.text === null) {
				ctx.addIssue({
					code: z.ZodIssueCode.invalid_type,
					expected: 'string',
					received: 'null',
					path: ['text'],
					message: 'text is required for paragraph and emphasis blocks',
				});
			}
		}
		if (data.type === 'heading') {
			if (data.text === null) {
				ctx.addIssue({
					code: z.ZodIssueCode.invalid_type,
					expected: 'string',
					received: 'null',
					path: ['text'],
					message: 'text is required for heading blocks',
				});
			}
			if (data.level === null) {
				ctx.addIssue({
					code: z.ZodIssueCode.invalid_type,
					expected: 'number',
					received: 'null',
					path: ['level'],
					message: 'level is required for heading blocks',
				});
			}
		}
		if (data.type === 'ordered_list' || data.type === 'unordered_list') {
			if (data.items === null) {
				ctx.addIssue({
					code: z.ZodIssueCode.invalid_type,
					expected: 'array',
					received: 'null',
					path: ['items'],
					message: 'items are required for list blocks',
				});
			}
		}
	});
export type ContentBlock = z.infer<typeof ContentBlockSchema>;

/**
 * Structured PDF content - the source of truth
 */
export const StructuredPdfContentSchema = z
	.object({
		blocks: z.array(ContentBlockSchema).min(1),
		metadata: z
			.object({
				format: z.literal('json'),
				version: z.string(),
				generatedAt: z.string(),
			})
			.strict(),
	})
	.strict();
export type StructuredPdfContent = z.infer<typeof StructuredPdfContentSchema>;

/**
 * Raw AI response (before parsing)
 * Can be JSON string or other formats
 */
export const RawAiResponseSchema = z.object({
	content: z.string(),
	format: z.enum(['json', 'markdown']).default('json'),
	model: z.string(),
	tokensUsed: z.number().int().nonnegative(),
});
export type RawAiResponse = z.infer<typeof RawAiResponseSchema>;
